'use client';
import { useEffect, useMemo } from 'react';
import { BaseContentResponse } from 'lib/services/models/content-response';

interface DataLayerEvent {
  event: string;
  [key: string]: string | number | boolean | string[] | null | undefined;
}

export const pushToDataLayer = (event: DataLayerEvent): void => {
  if (typeof window === 'undefined') return;
  try {
    const w = window as Window & { dataLayer?: DataLayerEvent[] };
    w.dataLayer ??= [];
    w.dataLayer.push(event);
  } catch {
    console.error('GTM push failed', event);
  }
};

export const buildContentResultPayload = ({
  content,
  isNumberedList,
  language,
  contentType,
  recordType,
}: {
  content: Partial<BaseContentResponse>;
  isNumberedList: boolean;
  language: string | undefined | null;
  contentType: string;
  recordType?: string;
}): DataLayerEvent => {
  const contentListName = isNumberedList ? 'SideBySide-NumberedList' : 'SideBySide-UnorderedList';
  const templateName = 'PandoSites/NumberedList';

  const contentTypeValue = contentType;
  const recordTypeValue = recordType;

  const payload: DataLayerEvent = {
    event: 'content_result',
    content_id: content?.ContentId || '',
    content_title: content?.Title || '',
    content_type: contentTypeValue,
    record_type: recordTypeValue,
    content_list_name: contentListName,
    language: (language || 'en').toLowerCase(),
    template_name: templateName,
  };

  return payload;
};

export function useContentResultAnalytics(
  content: Partial<BaseContentResponse>,
  isNumberedList: boolean,
  language?: string | null,
  contentType?: string,
  recordType?: string
): void {
  const payload = useMemo(
    () =>
      buildContentResultPayload({
        content,
        isNumberedList,
        language,
        contentType: contentType || 'Content',
        recordType: recordType || 'Content',
      }),
    [content, isNumberedList, language, contentType, recordType]
  );

  useEffect(() => {
    pushToDataLayer(payload);
  }, [payload]);
}
